import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, rolePortal } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Sprout, LogIn, Loader2, Wand2 } from "lucide-react";
import LoginWizard from "@/components/login-wizard";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@thanhson.vn");
  const [password, setPassword] = useState("password123");
  const [loading, setLoading] = useState(false);
  const [demo, setDemo] = useState([]);
  const [mode, setMode] = useState("login"); // "login" | "wizard"

  useEffect(() => {
    api.get("/auth/demo-accounts").then((r) => setDemo(r.data)).catch(() => {});
  }, []);

  if (user) return <Navigate to={rolePortal(user.role)} replace />;

  const submit = async (e, overrideEmail) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const u = await login(overrideEmail || email, password);
      toast.success(`Chào ${u.full_name}`);
      nav(rolePortal(u.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Đăng nhập thất bại");
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = (em) => {
    setEmail(em);
    setPassword("password123");
    submit(null, em);
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Left brand panel - hidden on mobile */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0F2A22 0%, #0F4D3A 50%, #186E54 100%)" }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1676557060416-1418aefb165d?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85')", backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "overlay" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-sm bg-white/10 backdrop-blur flex items-center justify-center border border-white/20">
              <Sprout className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-amber-300/80">Holding</div>
              <div className="font-serif text-2xl font-bold leading-none">Thanh Sơn</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-300 mb-3">ERP Hub-Satellite</div>
            <h1 className="font-serif text-5xl xl:text-6xl font-bold leading-[1.05] mb-5">
              Vận hành <em className="italic text-amber-300">chuỗi dọc</em><br/>
              từ nông trại tới bàn ăn.
            </h1>
            <p className="text-stone-200 text-base max-w-md leading-relaxed">
              Một nền tảng duy nhất cho HR-Ops, POS, kho-bếp, HTX và tài chính của toàn holding.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
            <Stat label="Pháp nhân" value="2" />
            <Stat label="Chi nhánh" value="3" />
            <Stat label="Nhân sự" value="10+" />
          </div>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-sm bg-emerald-900 flex items-center justify-center">
              <Sprout className="w-5 h-5 text-amber-300" />
            </div>
            <div className="font-serif text-xl font-bold">Thanh Sơn ERP</div>
          </div>

          <div className="text-xs uppercase tracking-[0.25em] text-emerald-800 font-bold mb-2">
            {mode === "wizard" ? "Wizard onboarding" : "Đăng nhập hệ thống"}
          </div>
          <h2 className="font-serif text-3xl font-bold mb-4">
            {mode === "wizard" ? "Bạn là ai?" : "Chào mừng trở lại."}
          </h2>

          <div className="flex gap-1 mb-5 p-1 rounded-sm bg-stone-100 border border-stone-200 text-xs font-semibold" data-testid="login-mode-tabs">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`flex-1 py-1.5 rounded-sm transition-colors flex items-center justify-center gap-1.5 ${mode === "login" ? "bg-white text-emerald-900 shadow-sm" : "text-stone-500 hover:text-stone-800"}`}
              data-testid="mode-login"
            >
              <LogIn className="w-3.5 h-3.5" /> Đăng nhập
            </button>
            <button
              type="button"
              onClick={() => setMode("wizard")}
              className={`flex-1 py-1.5 rounded-sm transition-colors flex items-center justify-center gap-1.5 ${mode === "wizard" ? "bg-white text-emerald-900 shadow-sm" : "text-stone-500 hover:text-stone-800"}`}
              data-testid="mode-wizard"
            >
              <Wand2 className="w-3.5 h-3.5" /> Wizard
            </button>
          </div>

          {mode === "wizard" ? (
            <LoginWizard />
          ) : (
          <>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-stone-700">Email</Label>
              <Input
                id="email" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@thanhson.vn"
                className="rounded-sm bg-white h-11"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw" className="text-stone-700">Mật khẩu</Label>
              <Input
                id="pw" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm bg-white h-11"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              className="w-full h-11 rounded-sm bg-emerald-800 hover:bg-emerald-900 text-white font-semibold"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
              Đăng nhập
            </Button>
          </form>

          {demo.length > 0 && (
            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-3 font-bold">
                ⚡ Đăng nhập nhanh (Demo)
              </div>
              <div className="grid grid-cols-2 gap-2">
                {demo.map((d) => (
                  <button
                    key={d.email}
                    type="button"
                    onClick={() => quickLogin(d.email)}
                    className="text-left p-2 rounded-sm bg-white border border-stone-200 hover:border-emerald-700 hover:bg-emerald-50 transition-colors group"
                    data-testid={`quick-login-${d.role}`}
                  >
                    <div className="text-xs font-semibold text-stone-800 group-hover:text-emerald-900">{d.label}</div>
                    <div className="text-[10px] text-stone-500 font-mono truncate">{d.email}</div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-stone-400 mt-3 font-mono">password: password123</p>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-serif text-3xl font-bold text-amber-300">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">{label}</div>
    </div>
  );
}
